/**
 * Store locator — filtering over the server-rendered list, plus a Google map
 * that is only fetched when the visitor asks for it.
 *
 * The section renders every store as an <li> carrying its coordinates and a
 * normalized search string, so this element never needs a second copy of the
 * data. Filtering is a class toggle over those elements; the map reads the same
 * attributes when it is built.
 *
 * The map builds itself as soon as the element upgrades, framed so every store
 * in the list is on screen at once, and follows the list from there: filter
 * down to one province and the map closes in on that province's stores.
 */

const MAPS_CALLBACK = '__eyelitStoreLocatorMapsReady';

/** Zoom used when a single store is all there is to frame. */
const STORE_ZOOM = 15;

/** Breathing room, in pixels, between the outermost pins and the map edge. */
const FRAME_PADDING = 48;

/** Folds accents and case so "Córdoba" matches a search for "cordoba". */
function normalize(value) {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Loads the Google Maps JS API once per page, no matter how many callers ask.
 * Resolves with the `google` namespace.
 */
let mapsPromise = null;
function loadGoogleMaps(apiKey) {
  if (mapsPromise) return mapsPromise;

  mapsPromise = new Promise((resolve, reject) => {
    window[MAPS_CALLBACK] = () => {
      delete window[MAPS_CALLBACK];
      resolve(window.google);
    };

    const script = document.createElement('script');
    script.src =
      'https://maps.googleapis.com/maps/api/js' +
      `?key=${encodeURIComponent(apiKey)}&loading=async&callback=${MAPS_CALLBACK}`;
    script.async = true;
    script.onerror = () => {
      mapsPromise = null;
      delete window[MAPS_CALLBACK];
      reject(new Error('Google Maps failed to load'));
    };
    document.head.appendChild(script);
  });

  return mapsPromise;
}

class EyelitStoreLocator extends HTMLElement {
  connectedCallback() {
    this.stores = Array.from(this.querySelectorAll('[data-locator-store]')).map((element) => ({
      element,
      province: element.dataset.province || '',
      haystack: normalize(element.dataset.search || ''),
      lat: Number(element.dataset.lat),
      lng: Number(element.dataset.lng),
      name: element.querySelector('.eyelit-locator__store-name')?.textContent.trim() || '',
      address: element.querySelector('[data-locator-address]')?.textContent.trim() || '',
    }));

    if (this.stores.length === 0) return;

    this.controls = this.querySelector('[data-locator-controls]');
    this.searchInput = this.querySelector('[data-locator-search]');
    this.provinceSelect = this.querySelector('[data-locator-province]');
    this.countOutput = this.querySelector('[data-locator-count]');
    this.noResults = this.querySelector('[data-locator-no-results]');
    this.mapWrap = this.querySelector('[data-locator-map-wrap]');
    this.mapOpenButton = this.querySelector('[data-locator-map-open]');
    this.mapContainer = this.querySelector('[data-locator-map]');

    this.markers = new Map();
    this.map = null;
    this.maps = null;
    this.mapPromise = null;
    this.infoWindow = null;

    this.sortByProvince();
    this.fillProvinces();
    this.bindFilters();
    this.bindMap();

    if (this.controls) this.controls.hidden = false;
    this.applyFilters();
  }

  /**
   * Metaobject entries come back in creation order, so the list is grouped here
   * rather than in Liquid — a loop over the entries can't be sorted without
   * fetching them again.
   */
  sortByProvince() {
    const list = this.querySelector('[data-locator-list]');
    if (!list) return;

    const collator = new Intl.Collator('es', { sensitivity: 'base' });
    const ordered = [...this.stores].sort(
      (a, b) => collator.compare(a.province, b.province) || collator.compare(a.name, b.name)
    );
    ordered.forEach((store) => list.appendChild(store.element));

    // Keep the array in the order the visitor reads, so "the first store" means
    // the same thing to the map as it does to the list.
    this.stores = ordered;
  }

  fillProvinces() {
    if (!this.provinceSelect) return;

    const collator = new Intl.Collator('es', { sensitivity: 'base' });
    const provinces = [...new Set(this.stores.map((store) => store.province))]
      .filter(Boolean)
      .sort(collator.compare);

    const options = document.createDocumentFragment();
    provinces.forEach((province) => {
      const option = document.createElement('option');
      option.value = province;
      option.textContent = province;
      options.appendChild(option);
    });
    this.provinceSelect.appendChild(options);

    // A province in the URL makes a filtered locator shareable.
    const requested = new URLSearchParams(window.location.search).get('provincia');
    if (requested && provinces.includes(requested)) {
      this.provinceSelect.value = requested;
    }
  }

  bindFilters() {
    let debounce;
    this.searchInput?.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => this.applyFilters(), 150);
    });

    this.provinceSelect?.addEventListener('change', () => {
      this.applyFilters();
      this.syncUrl();
    });
  }

  applyFilters() {
    const query = normalize(this.searchInput?.value || '');
    const province = this.provinceSelect?.value || '';

    let visible = 0;
    this.stores.forEach((store) => {
      const matches =
        (query === '' || store.haystack.includes(query)) &&
        (province === '' || store.province === province);

      store.element.hidden = !matches;
      store.visible = matches;
      if (matches) visible += 1;
    });

    if (this.noResults) this.noResults.hidden = visible !== 0;
    if (this.countOutput) {
      this.countOutput.textContent = this.countOutput.dataset.template
        ? this.countOutput.dataset.template.replace('__COUNT__', visible)
        : String(visible);
    }

    this.syncMarkers();
    this.frameVisibleStores();
  }

  /**
   * Frames whatever the list is showing. On arrival that is every store, so the
   * visitor opens the page on a map of Argentina with all the pins in view;
   * after a filter it is the matching set, so the map closes in on that
   * province instead of sitting somewhere the list no longer mentions.
   *
   * A single match has no extent to fit — `fitBounds` on one point zooms to the
   * maximum — so it gets a fixed zoom instead.
   *
   * A search that matches nothing frames every store. Typing narrows the list a
   * keystroke at a time, so the frame before the one that empties it is usually
   * a single store at zoom 15: leaving it there left an empty street map of
   * whatever town was matched a letter ago, next to a message saying there is
   * nothing. Pulling back to the whole country says the same thing as the
   * message does.
   */
  frameVisibleStores() {
    if (!this.map || !this.maps) return;

    const located = this.stores.filter(
      (store) => !Number.isNaN(store.lat) && !Number.isNaN(store.lng)
    );
    const matching = located.filter((store) => store.visible !== false);
    const frame = matching.length > 0 ? matching : located;
    if (frame.length === 0) return;

    if (frame.length === 1) {
      this.map.setCenter({ lat: frame[0].lat, lng: frame[0].lng });
      this.map.setZoom(STORE_ZOOM);
      return;
    }

    const bounds = new this.maps.LatLngBounds();
    frame.forEach((store) => bounds.extend({ lat: store.lat, lng: store.lng }));
    this.map.fitBounds(bounds, FRAME_PADDING);
  }

  syncUrl() {
    const url = new URL(window.location.href);
    const province = this.provinceSelect?.value || '';

    if (province) url.searchParams.set('provincia', province);
    else url.searchParams.delete('provincia');

    window.history.replaceState({}, '', url);
  }

  bindMap() {
    if (!this.mapContainer) return;

    this.mapOpenButton?.addEventListener('click', () => this.openMap());

    this.querySelectorAll('[data-locator-show-on-map]').forEach((button) => {
      button.hidden = false;
      button.addEventListener('click', () => {
        const element = button.closest('[data-locator-store]');
        const store = this.stores.find((candidate) => candidate.element === element);
        if (store) this.focusStore(store);
      });
    });

    // The map is part of the page, not something to ask for. It is still built
    // from JavaScript, so the list is painted and readable before Google's
    // script is even requested.
    this.openMap();
  }

  /** Builds the map once; concurrent callers share the same promise. */
  openMap() {
    if (!this.mapPromise) this.mapPromise = this.createMap();
    return this.mapPromise;
  }

  async createMap() {
    const apiKey = this.dataset.apiKey;
    if (!apiKey || !this.mapContainer) return;

    if (this.mapOpenButton) this.mapOpenButton.disabled = true;

    try {
      const google = await loadGoogleMaps(apiKey);
      // Kept so framing can build a LatLngBounds without reaching for a global.
      this.maps = google.maps;

      this.mapContainer.hidden = false;
      this.map = new google.maps.Map(this.mapContainer, {
        center: {
          lat: Number(this.dataset.defaultLat) || 0,
          lng: Number(this.dataset.defaultLng) || 0,
        },
        zoom: Number(this.dataset.defaultZoom) || 4,
        mapTypeControl: false,
        streetViewControl: false,
      });
      this.infoWindow = new google.maps.InfoWindow();

      this.stores.forEach((store) => {
        if (Number.isNaN(store.lat) || Number.isNaN(store.lng)) return;

        const marker = new google.maps.Marker({
          position: { lat: store.lat, lng: store.lng },
          title: store.name,
          icon: this.dataset.marker || undefined,
        });
        marker.addListener('click', () => this.showInfo(store, marker));
        this.markers.set(store.element, marker);
      });

      this.syncMarkers();
      this.frameVisibleStores();
      if (this.mapOpenButton) this.mapOpenButton.hidden = true;
    } catch (error) {
      // Google is unreachable — offer the button so the visitor can retry
      // rather than leaving an empty column where the map should be.
      this.mapPromise = null;
      if (this.mapOpenButton) {
        this.mapOpenButton.hidden = false;
        this.mapOpenButton.disabled = false;
      }
      console.error('[eyelit-store-locator]', error);
    }
  }

  /** Keeps the pins in step with the list: a filtered-out store loses its pin. */
  syncMarkers() {
    if (!this.map) return;

    this.markers.forEach((marker, element) => {
      const store = this.stores.find((candidate) => candidate.element === element);
      marker.setMap(store && store.visible !== false ? this.map : null);
    });
  }

  async focusStore(store) {
    if (!this.map) await this.openMap();
    if (!this.map) return;

    this.map.panTo({ lat: store.lat, lng: store.lng });
    this.map.setZoom(15);
    this.mapContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    const marker = this.markers.get(store.element);
    if (marker) this.showInfo(store, marker);
  }

  showInfo(store, marker) {
    if (!this.infoWindow) return;

    const content = document.createElement('div');
    content.className = 'eyelit-locator__info';

    const name = document.createElement('strong');
    name.textContent = store.name;
    content.appendChild(name);

    if (store.address) {
      const address = document.createElement('p');
      address.textContent = store.address;
      content.appendChild(address);
    }

    this.infoWindow.setContent(content);
    this.infoWindow.open({ map: this.map, anchor: marker });
  }
}

if (!customElements.get('eyelit-store-locator')) {
  customElements.define('eyelit-store-locator', EyelitStoreLocator);
}
